import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsButton from '../LdsButton/index.jsx';
import LdsIcon from '../LdsIcon/index.jsx';

import transparency from '../../assets/transparency.png';

import {
  validateBannerImageAltText,
  getContentAlignment,
  isScrollable,
  validateScrollButtonClasses,
  getScrollButtonClasses,
  getScrollButtonIconName,
  scrollDown
} from './hero.helpers.js';

function LdsHero({
  id,
  images,
  alt,
  contentAlign,
  useScrollButton = false,
  scrollButtonLabel = 'Scroll Down',
  scrollButtonClasses,
  scrollButtonIcon,
  className,
  classes,
  children,
  ...rest
}) {
  const scrollable = isScrollable(id, useScrollButton);

  const ScrollButton = () => {
    if (!scrollable) return '';

    const buttonClasses = getScrollButtonClasses(scrollButtonClasses);
    const iconName = getScrollButtonIconName(scrollButtonIcon);

    function clickScrollButton() {
      scrollDown(id);
    }

    return (
      <div className="lds-hero-scroll">
        <LdsButton
          classes={cn(
            'lds-hero-scroll-button',
            {
              [`${buttonClasses}`]: buttonClasses,
            }
          )}
          onClick={clickScrollButton}
        >
          <span className="lds-hero-scroll-button-label">
            {scrollButtonLabel}
          </span>
          <span className="lds-hero-scroll-button-icon">
            <LdsIcon
              name={iconName}
              inline
              decorative
            >
            </LdsIcon>
          </span>
        </LdsButton>
      </div>
    );
  };

  const contentAlignment = getContentAlignment(contentAlign);

  return (
    <section
      id={id}
      className={cn(
        'lds-hero',
        {
          [`${contentAlignment}`]: contentAlignment,
          'use-scroll-button': scrollable,
        },
        className,
        classes
      )}
      {...rest}
    >
      <picture className="lds-hero-image">
        { images && Array.isArray(images) && images.map((image, i) => (
          <source
            key={i}
            media={image.media}
            srcSet={image.srcset}
          />
        ))}
        <img
          className="lds-hero-image-fallback"
          style={{ backgroundImage: `url(${images[images.length - 1].srcset})` }}
          src={transparency}
          alt={alt}
        />
      </picture>
      <div className="wrapper">
        <div className="lds-hero-body section row align-items-center">
          <div className="col">
            {children}
          </div>
        </div>
      </div>
      <ScrollButton></ScrollButton>
    </section>
  );
}

export default LdsHero;

LdsHero.propTypes = {
  id: PropTypes.string,
  contentAlign: PropTypes.oneOf(['center', 'left', 'right', undefined]),
  useScrollButton: PropTypes.bool,
  scrollButtonLabel: PropTypes.string,
  scrollButtonClasses: (props, propName, componentName) => validateScrollButtonClasses(props[propName], componentName),
  scrollButtonIcon: PropTypes.oneOf(['chevron', 'triangle', 'arrow']),
  alt: (props, propName, componentName) => validateBannerImageAltText(props[propName], componentName),
  images: PropTypes.arrayOf((propValue, key) => { // eslint-disable-line
    if (!propValue[key].srcset) {
      return new Error(`\n
      >>> LDS ERROR: [Invalid Component Data] in LdsHero: banner images array object missing 'srcset' property at index ${key} (${key + 1}/${propValue.length}).
      Each responsive image object must contain an 'srcset' property.
      `);
    }
    if (!propValue[key].media && key !== (propValue.length - 1)) {
      return new Error(`\n
      >>> LDS ERROR: [Invalid Component Data] in LdsHero: banner images array object missing 'media' property at index ${key} (${key + 1}/${propValue.length}).
      Each responsive image object EXCEPT the fallback default must contain a 'media' property.
      `);
    }
    if (propValue[key].media && key === (propValue.length - 1)) {
      return new Error(`\n
      >>> LDS ERROR: [Invalid Component Data] in LdsHero: banner images array default fallback object contains an invalid 'media' property at index ${key} (${key + 1}/${propValue.length}).
      The fallback responsive image object MUST always be the last object in the array and MUST NOT contain a 'media' property.
      `);
    }
  }).isRequired,
};
